import { PRICING, SITE, STATS } from "../constants/site";

// Reusable JSON-LD builders for SEO structured data.
// Keeping these in one place keeps every page consistent for search engines and LLMs.

const ORG_ID = `${SITE.domain}/#organization`;
const SITE_ID = `${SITE.domain}/#website`;

export function organizationSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: SITE.name,
    legalName: SITE.legalName,
    alternateName: ["VerifyDeceased.com", "Verify Deceased"],
    url: SITE.domain,
    logo: {
      "@type": "ImageObject",
      url: `${SITE.domain}/og-image.png`,
      width: 1200,
      height: 630
    },
    image: `${SITE.domain}/og-image.png`,
    description: SITE.metaDescription,
    email: SITE.email,
    telephone: SITE.phone,
    foundingDate: SITE.foundingYear,
    areaServed: {
      "@type": "Country",
      name: "United States"
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.addressParts.street,
      addressLocality: SITE.addressParts.city,
      addressRegion: SITE.addressParts.region,
      postalCode: SITE.addressParts.postalCode,
      addressCountry: SITE.addressParts.country
    },
    parentOrganization: {
      "@type": "Organization",
      name: SITE.parent,
      url: SITE.parentUrl
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: SITE.email,
        telephone: SITE.phone,
        areaServed: "US",
        availableLanguage: "English"
      },
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: SITE.supportEmail,
        areaServed: "US",
        availableLanguage: "English"
      }
    ],
    sameAs: [SITE.social.linkedin, SITE.social.x, SITE.social.facebook]
  };
}

export function websiteSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": SITE_ID,
    name: SITE.name,
    alternateName: SITE.legalName,
    url: SITE.domain,
    description: SITE.metaDescription,
    inLanguage: "en-US",
    publisher: { "@id": ORG_ID }
  };
}

export function webPageSchema(page: {
  name: string;
  description: string;
  path: string;
  type?: string;
}): Record<string, unknown> {
  const url = `${SITE.domain}${page.path}`;
  return {
    "@context": "https://schema.org",
    "@type": page.type ?? "WebPage",
    "@id": `${url}#webpage`,
    url,
    name: page.name,
    description: page.description,
    inLanguage: "en-US",
    isPartOf: { "@id": SITE_ID },
    about: { "@id": ORG_ID },
    primaryImageOfPage: `${SITE.domain}/og-image.png`
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE.domain}${item.path}`
    }))
  };
}

export function faqSchema(faqs: { question: string; answer: string }[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
}

export function serviceSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Deceased consumer verification",
    serviceType: "Deceased consumer verification",
    provider: { "@id": ORG_ID },
    areaServed: {
      "@type": "Country",
      name: "United States"
    },
    audience: {
      "@type": "BusinessAudience",
      name: "Banks, lenders, card issuers, insurers and fraud prevention teams"
    },
    description: SITE.metaDescription,
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: PRICING.standardPerRecord.toFixed(2),
      description: `Per verified record, with bulk pricing from $${PRICING.bulkPerRecord.toFixed(2)} per record.`,
      availability: "https://schema.org/InStock",
      url: `${SITE.domain}/pricing`
    }
  };
}

export function softwareApplicationSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE.name,
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Identity verification and fraud prevention",
    operatingSystem: "Web browser, REST API",
    url: SITE.domain,
    description: SITE.metaDescription,
    publisher: { "@id": ORG_ID },
    featureList: [
      "Single record deceased verification",
      "Bulk CSV and Excel batch verification",
      "REST API for real time checks",
      `Nationwide coverage across ${STATS.statesCovered} states`,
      `Death records refreshed every ${STATS.dataFreshnessDays} days`
    ],
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: PRICING.standardPerRecord.toFixed(2),
      description: "Pay per verified record. No subscription required."
    }
  };
}

export function offerCatalogSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: `${SITE.name} deceased record verification`,
    description:
      "Pay per verified record deceased consumer verification with nationwide coverage and weekly data refreshes.",
    brand: { "@id": ORG_ID },
    image: `${SITE.domain}/og-image.png`,
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: PRICING.bulkPerRecord.toFixed(2),
      highPrice: PRICING.standardPerRecord.toFixed(2),
      offerCount: 2,
      url: `${SITE.domain}/pricing`,
      availability: "https://schema.org/InStock",
      offers: [
        {
          "@type": "Offer",
          name: "Standard",
          priceCurrency: "USD",
          price: PRICING.standardPerRecord.toFixed(2),
          description: "Per verified record for single and batch lookups."
        },
        {
          "@type": "Offer",
          name: "Bulk",
          priceCurrency: "USD",
          price: PRICING.bulkPerRecord.toFixed(2),
          description: `Per verified record above ${PRICING.bulkThreshold.toLocaleString("en-US")} records.`
        }
      ]
    }
  };
}

export function howToSchema(steps: { name: string; text: string }[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to verify whether a consumer is deceased",
    description:
      "Upload or submit consumer records, match them against nationwide death data, then review confirmed matches.",
    totalTime: "PT5M",
    step: steps.map((step, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: step.name,
      text: step.text,
      url: `${SITE.domain}/how-it-works#step-${index + 1}`
    }))
  };
}

export function leadershipSchema(
  people: { name: string; role: string; linkedIn: string }[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: SITE.name,
    url: SITE.domain,
    employee: people.map(person => ({
      "@type": "Person",
      name: person.name,
      jobTitle: person.role,
      worksFor: { "@id": ORG_ID },
      ...(person.linkedIn ? { sameAs: [person.linkedIn] } : {})
    }))
  };
}

export function articleSchema(article: {  slug: string;
  title: string;
  metaDescription: string;
  keywords: string;
  published: string;
  updated: string;
  readMinutes: number;
}): Record<string, unknown> {
  const url = `${SITE.domain}/resources/${article.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    headline: article.title,
    description: article.metaDescription,
    keywords: article.keywords,
    url,
    mainEntityOfPage: url,
    datePublished: article.published,
    dateModified: article.updated,
    inLanguage: "en-US",
    image: `${SITE.domain}/og-image.png`,
    timeRequired: `PT${article.readMinutes}M`,
    author: { "@id": ORG_ID },
    publisher: { "@id": ORG_ID },
    isPartOf: { "@id": SITE_ID }
  };
}

export function articleListSchema(
  articles: { slug: string; title: string }[]
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${SITE.name} resource library`,
    itemListElement: articles.map((article, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: article.title,
      url: `${SITE.domain}/resources/${article.slug}`
    }))
  };
}

export function contactPageSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": `${SITE.domain}/contact#webpage`,
    url: `${SITE.domain}/contact`,
    name: `Contact ${SITE.name}`,
    inLanguage: "en-US",
    isPartOf: { "@id": SITE_ID },
    mainEntity: { "@id": ORG_ID }
  };
}
